import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Customer, CustomerService } from '../../services/customer';
import { PendingOrderService, PendingSalesOrder } from '../../services/pending-order-service';
import { CreateSalesOrderPayload, SalesOrderLine, SalesOrderService } from '../../services/sales-order';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { NotificationService } from '../../services/notification';
import { Router } from '@angular/router';
import { PendingOrderSelectionService } from '../../services/pending-order-selection';
import { MessageDialogService } from '../../services/message-dialog';
import { ProductService, normalizeSaleItems } from '../../services/products';

@Component({
  selector: 'app-pending-orders',
  templateUrl: './pending-orders-component.html',
  styleUrls: ['./pending-orders-component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
  ],
})
export class PendingOrdersComponent implements OnInit {
  displayedColumns: string[] = ['customerName', 'numberOfItems', 'totalAmount']; // Columns to display
  footerColumns: string[] = ['grandTotDesc', 'grandTotal'];
  pendingOrders: PendingSalesOrder[] = []; // Use a simple array for pending orders
  grandTotal = 0;
  constructor(
    private pendingOrderService: PendingOrderService,
    private customerService: CustomerService, 
    private salesOrderService: SalesOrderService,
    private notificationService: NotificationService,
    private pendingOrderSelectionService: PendingOrderSelectionService,
    private messageDialog: MessageDialogService,
    private productService: ProductService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadPendingOrders();  // Load pending orders when the component initializes
  }

  // Fetch pending orders and transform them to include additional details
  async loadPendingOrders(): Promise<void> {
    const orders = await this.pendingOrderService.getPendingOrders();
    this.grandTotal = 0;
    this.pendingOrders = await Promise.all(orders.map(async (order) => {
      const customer = await this.getCustomer(order.salesOrder.partner_id, order.salesOrder.partner_client_uuid);
      const numberOfItems = this.calculateOrderItems(order.salesOrder);
      const totalAmount = this.calculateOrderTotal(order.salesOrder);
      this.grandTotal += totalAmount;
      return {
        ...order,
        customerName: customer?.name ?? 'Unknown',
        totalAmount,
        orderDate: new Date(), // Use actual order date if available
        numberOfItems,
        orderDetails: order, // Save the entire order data for later use
      };
    }));
  }

  async getCustomer(customerId: number, clientUuid?: string): Promise<Customer | undefined> {
    const customers = await firstValueFrom(this.customerService.get());
    return customers.find(c => c.id === customerId || (!!clientUuid && c.clientCustomerUuid === clientUuid));
  }

  // Calculate total items from the order lines
  calculateOrderItems(order: CreateSalesOrderPayload): number {
    return order.order_line.reduce((sum, line: SalesOrderLine) => sum + line.product_uom_qty, 0);
  }

  // Calculate the total order amount
  calculateOrderTotal(order: CreateSalesOrderPayload): number {
    return order.order_line.reduce((total, line: SalesOrderLine) => {
      return total + (line.product_uom_qty * line.price_unit);
    }, 0);
  }

  // Sync Orders method
  async syncOrders(): Promise<void> {
    this.notificationService.showMessage('Syncing customers and orders...');
    await this.customerService.syncPendingCustomers();
    const products = await firstValueFrom(this.productService.get());

    // Iterate through each pending order. Customer dependency is resolved first.
    for (let i = 0; i < this.pendingOrders.length; i++) {
      try {
        const pendingOrder = this.pendingOrders[i];
        if (pendingOrder.salesOrder.partner_id <= 0 && pendingOrder.salesOrder.partner_client_uuid) {
          const resolvedCustomer = await this.customerService.findByClientUuid(pendingOrder.salesOrder.partner_client_uuid);
          if (!resolvedCustomer || resolvedCustomer.id <= 0 || resolvedCustomer.pendingSync) {
            console.warn('Skipping order until its customer is synced.', pendingOrder.salesOrder.idempotencyKey);
            continue;
          }
          pendingOrder.salesOrder.partner_id = resolvedCustomer.id;
          await this.pendingOrderService.updatePendingOrder(pendingOrder);
        }

        const normalizedItems = normalizeSaleItems(
          pendingOrder.salesOrder.order_line.map(line => ({
            productId: line.product_id,
            quantity: line.product_uom_qty,
            price: line.price_unit
          })),
          products
        );
        pendingOrder.salesOrder.order_line = normalizedItems.map(item => ({
          product_id: item.productId,
          product_uom_qty: item.quantity,
          price_unit: item.price
        }));

        // Call the createOrder API for each order
        const response = await firstValueFrom(this.salesOrderService.createOrder(pendingOrder.salesOrder));
        console.log('Order synced successfully!', response);

        // Remove successfully synced order from the pendingOrders array
        this.pendingOrders.splice(i, 1);  // Remove the current order at index 'i'
        i--;  // Adjust index to account for the removed item

      } catch (err: any) {
        console.error('Failed to sync order', err);
        if (err?.status === 400 && err?.error?.code === 'minimum_price_violation') {
          this.messageDialog.error('Product pricing changed. Sync products and try this pending order again.');
        }
        // No need to push to failedOrders, just skip on failure
      }
    }
    if (this.pendingOrders.length > 0) 
      this.notificationService.showMessage('Sync process complete with some errors.');
    else
      this.notificationService.showMessage('Sync process complete.');

    this.pendingOrderService.updatePendingOrders(this.pendingOrders); // Update pending orders after sync
  }

  openOrder(order: PendingSalesOrder): void {
    this.pendingOrderSelectionService.set(order);
    this.router.navigate(['/view-pending-order']);
  }

}
